import { useId, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, Icon } from '@bloo-team/ui'
import { Copy, History, Search, Star, X } from '@bloo-team/ui/icons'
import { entityIcon } from '../entity-icon'
import { copyText, entitySearchQuery } from './SignalPanel'
import { StorylineMitre } from './StorylineMitre'
import { absTime, monthDayTime } from '../../lib/local-time'
import { likelihoodBand, mitreRows } from '../../lib/signal-attribution'
import { entityKey, type SignalEntity } from '../../lib/signal-model'
import { STORY_TABS, storylineRows, type StoryTab, type SubgraphEntity } from '../../lib/signal-subgraphs'
import type { StorylinePanelProps } from './types'

/**
 * The storyline drawer (docs/signals.md §Storyline panel) — the docked pane
 * for one connected-signals subgraph. Header: severity edge, score, last
 * update, counts, the case it maps to, Add to case. Tabs:
 * Signals (every member its own row → the signal pane) · Artifacts (entities by role, with the
 * chip pivots) · Campaigns (attribution; a toggle per campaign) · MITRE
 * (a tactic-coverage summary; Expand opens the matrix — StorylineMitre).
 * Props in, events out; only the campaign selection and the read-more
 * expansion are local (the page remounts the panel per storyline).
 */

const TAB_LABEL: Record<StoryTab, string> = {
  signals: 'Signals',
  artifacts: 'Artifacts',
  campaigns: 'Campaigns',
  mitre: 'MITRE',
}

const ROLE_LABEL: Record<SubgraphEntity['role'], string> = { target: 'targets', suspect: 'suspects', compromised: 'compromised' }
const ROLES: SubgraphEntity['role'][] = ['target', 'suspect', 'compromised']

/** A subgraph entity as the chip pivots take it; compromised = a target that also acted. */
function asSignalEntity(e: SubgraphEntity): SignalEntity {
  const field = e.field ? e.field.charAt(0).toUpperCase() + e.field.slice(1) : 'Entity'
  return { role: e.role === 'suspect' ? 'Suspect' : 'Target', field, value: e.value }
}

/** The title with its entity in mono (`… Host: <b>fin-srv-07</b>`), when the name ends with it. */
function Title({ title, entity }: { title: string; entity: string | null }) {
  if (entity && title.endsWith(entity) && title.length > entity.length) {
    return (
      <>
        {title.slice(0, title.length - entity.length)}
        <span className="mono">{entity}</span>
      </>
    )
  }
  return <>{title}</>
}

export function StorylinePanel({
  storyline: st,
  tab,
  onTab,
  resolveSignal,
  openUuid,
  campaigns,
  campaignsLoading,
  campaignsError,
  onRetryCampaigns,
  tacticOrder,
  watched,
  onOpenSignal,
  onOpenEntity,
  onOpenSearch,
  onToggleWatch,
  onAddToCase,
  onClose,
}: StorylinePanelProps) {
  const uid = useId()
  /** Campaign names toggled OFF — every campaign starts selected. */
  const [off, setOff] = useState<ReadonlySet<string>>(new Set())
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const selected = useMemo(() => new Set((campaigns ?? []).map((c) => c.name).filter((n) => !off.has(n))), [campaigns, off])

  /** Every member its own row — the job's merges undone (storylineRows). */
  const members = useMemo(() => storylineRows(st, resolveSignal), [st, resolveSignal])
  const mitre = useMemo(() => mitreRows(st, campaigns ?? [], selected, tacticOrder), [st, campaigns, selected, tacticOrder])
  const techniqueCount = mitre.reduce((n, r) => n + r.techniques.length, 0)
  /** Signals each entity links to on the canvas. */
  const linkCount = useMemo(() => {
    const out = new Map<string, number>()
    for (const l of st.links) {
      const [sig, ent] = l.from.startsWith('sig:') ? [l.from, l.to] : l.to.startsWith('sig:') ? [l.to, l.from] : [null, null]
      if (sig && ent) out.set(ent, (out.get(ent) ?? 0) + 1)
    }
    return out
  }, [st.links])

  const count: Partial<Record<StoryTab, number | null>> = {
    signals: st.uuids.length,
    artifacts: st.entities.length,
    campaigns: campaignsLoading ? null : (campaigns?.length ?? null),
    mitre: techniqueCount,
  }

  const toggle = (set: ReadonlySet<string>, k: string) => {
    const next = new Set(set)
    if (next.has(k)) next.delete(k)
    else next.add(k)
    return next
  }

  /* ---------------------------------------------------------------- tabs */

  const signalsTab = (
    <ol className="sp-timeline">
      {members.map((m) => {
        const ents = m.entities.map((e) => `${e.role.toLowerCase()} ${e.field.toLowerCase()} ${e.value}`).join(' · ')
        return (
          <li key={m.uuid}>
            <button type="button" className="sp-tl-row sl-sig-row" aria-current={openUuid === m.uuid ? 'true' : undefined} onClick={() => onOpenSignal(m.uuid)}>
              <span className="sp-sev" data-severity={m.severity} />
              <span className="sp-tl-name">{m.name || 'signal'}</span>
              <span className="sp-tl-time" title={m.at !== null ? absTime(m.at) : undefined}>
                {m.at !== null ? monthDayTime(m.at) : '—'}
              </span>
              <span className="sl-sig-ents" title={ents || undefined}>
                {ents || '—'}
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )

  const artifactsTab = (
    <div className="hud-stack sl-artifacts">
      {ROLES.map((role) => {
        const list = st.entities.filter((e) => e.role === role)
        if (!list.length) return null
        return (
          <section key={role} aria-label={ROLE_LABEL[role]}>
            <span className="hud-eyebrow">
              {ROLE_LABEL[role]} · {list.length}
            </span>
            <ul className="sp-entities">
              {list.map((raw) => {
                const e = asSignalEntity(raw)
                const key = entityKey(e)
                const n = linkCount.get(raw.id) ?? 0
                const on = watched.has(key)
                return (
                  <li key={raw.id} className="sp-entity" data-role={e.role}>
                    <span className="sp-entity-role" aria-hidden="true">
                      <Icon icon={entityIcon(e.field)} size={12} />
                    </span>
                    <span className="sp-entity-field">{e.field.toLowerCase()}</span>
                    <span className="sp-entity-value">{e.value}</span>
                    <span className="sp-entity-n">
                      {n} signal{n === 1 ? '' : 's'}
                    </span>
                    <span className="sp-entity-pivots">
                      <button type="button" className="qbc-x" aria-label={`Entity history for ${e.field} ${e.value}`} title="Entity history" onClick={() => onOpenEntity(e)}>
                        <Icon icon={History} size={11} />
                      </button>
                      <button
                        type="button"
                        className="qbc-x"
                        aria-label={`Search events for ${e.field} ${e.value}`}
                        title="Search events"
                        onClick={() => onOpenSearch(entitySearchQuery(e))}
                      >
                        <Icon icon={Search} size={11} />
                      </button>
                      <button
                        type="button"
                        className="qbc-x sl-watch"
                        aria-pressed={on}
                        aria-label={`${on ? 'Unwatch' : 'Watch'} ${e.field} ${e.value}`}
                        title={on ? 'Unwatch' : 'Watch'}
                        onClick={() => onToggleWatch(key)}
                      >
                        <Icon icon={Star} size={11} />
                      </button>
                      <button type="button" className="qbc-x" aria-label={`Copy value ${e.value}`} title="Copy value" onClick={() => copyText(e.value)}>
                        <Icon icon={Copy} size={11} />
                      </button>
                    </span>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
      {st.entities.length === 0 ? <p className="q-facet-note">No entities on this storyline.</p> : null}
    </div>
  )

  const campaignsTab = campaignsLoading ? (
    <p className="hud-unit" role="status">
      Attributing campaigns…
    </p>
  ) : campaignsError ? (
    <div className="bui-alert bui-alert-error" role="alert">
      <span className="bui-alert-mark">✕</span>
      <span>
        <span className="bui-alert-title">Attribution failed</span>
        {campaignsError}
      </span>
      <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={onRetryCampaigns}>
        Retry
      </button>
    </div>
  ) : !campaigns?.length ? (
    <p className="q-facet-note">No campaign matches this storyline's detections.</p>
  ) : (
    <div className="hud-stack">
      <ul className="sl-campaigns">
        {campaigns.map((c) => {
          const band = likelihoodBand(c.likelihood)
          const on = selected.has(c.name)
          const open = expanded.has(c.name)
          const techniques = [...new Set(Object.values(c.techniques).flat())]
          const id = `${uid}-c-${c.name}`
          return (
            <li key={c.name} className="sl-campaign" data-selected={on || undefined}>
              <div className="sl-campaign-head">
                <input id={id} type="checkbox" checked={on} onChange={() => setOff((s) => toggle(s, c.name))} />
                <label htmlFor={id} className="sl-campaign-name">
                  {c.name}
                </label>
                <span className="sl-likelihood" data-band={band}>
                  <span className="mono">{Math.round(c.likelihood)}%</span> <span className="hud-eyebrow">likelihood</span>
                </span>
              </div>
              <span className="sl-bar" data-band={band} aria-hidden="true">
                <span style={{ width: `${Math.min(100, Math.max(0, c.likelihood))}%` }} />
              </span>
              {techniques.length ? (
                <span className="sl-techniques">
                  {techniques.map((t) => (
                    <span key={t} className="sl-technique">
                      {t}
                    </span>
                  ))}
                </span>
              ) : null}
              {c.description ? (
                <>
                  <p className="sl-desc" data-open={open || undefined}>
                    {c.description}
                  </p>
                  <button type="button" className="list-chip" aria-expanded={open} onClick={() => setExpanded((s) => toggle(s, c.name))}>
                    {open ? 'read less' : 'read more →'}
                  </button>
                </>
              ) : null}
            </li>
          )
        })}
      </ul>
      <p className="q-facet-note">Attribution from the detection-to-campaign mapping · never applied automatically. Selected campaigns colour the MITRE tab.</p>
    </div>
  )

  const mitreTab = (
    <StorylineMitre
      rows={mitre}
      tacticOrder={tacticOrder}
      selectedCampaigns={selected.size}
      signals={st.signals}
      openUuid={openUuid}
      onOpenSignal={onOpenSignal}
    />
  )

  const tabBody: Record<StoryTab, ReactNode> = {
    signals: signalsTab,
    artifacts: artifactsTab,
    campaigns: campaignsTab,
    mitre: mitreTab,
  }

  /* -------------------------------------------------------------- layout */

  return (
    <aside className="sg-pane sl-pane" aria-label="Storyline">
      <div className="sg-pane-head sl-head">
        <div className="sg-pane-titles">
          <span className="hud-eyebrow sg-pane-eyebrow">storyline · {st.key}</span>
          <h2 className="sg-pane-title">
            <Title title={st.title} entity={st.entityName} />
          </h2>
        </div>
        <button type="button" className="qbc-x" aria-label="Close storyline" title="Close" onClick={onClose}>
          <Icon icon={X} size={14} />
        </button>
      </div>
      <div className="sg-pane-body">
        <div className="hud-stack sl-summary">
          <div className="hud-row sl-chips">
            <span className="sg-sev">
              <span className="sg-sev-dot" data-severity={st.severity} aria-hidden="true" />
              {st.severity}
            </span>
            <span className="sl-chip mono">score {st.score}</span>
            {st.promoted ? <span className="sg-badge">storyline</span> : null}
            <span className="sl-chip mono">updated {monthDayTime(st.last)}</span>
          </div>
          <dl className="sl-counts">
            {(
              [
                ['targets', st.counts.targets],
                ['suspects', st.counts.suspects],
                ['compromised', st.counts.compromised],
                ['signals', st.counts.signals],
                ['tactics', st.tactics.length],
                ['techniques', st.techniques.length],
              ] as const
            ).map(([label, n]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{n}</dd>
              </div>
            ))}
          </dl>
          <div className="hud-row sl-actions">
            <Button variant="solid" size="sm" onPress={onAddToCase}>
              Add to case
            </Button>
          </div>
        </div>

        <div className="bui-tabs sl-tabs" role="tablist" aria-label="Storyline views">
          {STORY_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`${uid}-tab-${t}`}
              aria-controls={`${uid}-panel`}
              aria-selected={tab === t}
              className="bui-tab"
              onClick={() => onTab(t)}
            >
              {TAB_LABEL[t]}
              {count[t] !== undefined && count[t] !== null ? <span className="sg-queue-n">{count[t]}</span> : null}
            </button>
          ))}
        </div>
        <div id={`${uid}-panel`} role="tabpanel" aria-labelledby={`${uid}-tab-${tab}`} className="sl-panel">
          {tabBody[tab]}
        </div>
      </div>
    </aside>
  )
}
